'use client';

import { useLenis } from 'lenis/react';
import Link from 'next/link';
import { useMemo } from 'react';
import { Navigation } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import { z } from 'zod';

import TriangleLeft from '~/assets/icons/triangle--left.svg';
import TriangleRight from '~/assets/icons/triangle--right.svg';
import Media from '~/components/molecules/Media';
import { modelPropertyType } from '~/lib/api/models';
import routeGenerator from '~/lib/api/routes';
import { useHydrationSafeResponsive } from '~/lib/hooks/useResponsive';
import { cn } from '~/lib/utils/classname';
import { getMainPropertyCategory } from '~/lib/utils/properties';

export type Props = {
  maxSlides?: number;
} & z.infer<
  typeof modelPropertyType.schema
>['propertyType']['properties']['edges'][number]['node'];

const PropertyTeaseSwiper: React.FC<Props> = ({
  maxSlides = 4,
  ...property
}) => {
  const { slug, propertyMeta } = property;

  const isDesktop = useHydrationSafeResponsive('desktop');
  const lenis = useLenis();

  const gallery = useMemo(
    () =>
      propertyMeta.imagesGallery.edges &&
      propertyMeta.imagesGallery.edges.length > 0 &&
      propertyMeta.imagesGallery.edges
        .filter((edge) => edge.node.mediaType === 'image')
        .slice(0, maxSlides),
    [maxSlides, propertyMeta.imagesGallery.edges]
  );

  return (
    <Swiper
      className="pointer-events-none relative z-10 col-span-3 col-start-1 row-start-1 h-full w-full overflow-visible! [&>.swiper-wrapper>.swiper-slide]:w-auto!"
      modules={[Navigation]}
      slidesPerView="auto"
      spaceBetween={8}
      lazyPreloadPrevNext={1}
      grabCursor
      navigation={{
        prevEl: '.swiper-button--prev',
        nextEl: '.swiper-button--next',
      }}
      onSliderMove={() => lenis && lenis.stop()}
      onTouchEnd={() => lenis && lenis.start()}
      onDragEnd={() => lenis && lenis.start()}
    >
      {isDesktop && (
        <SwiperSlide>
          <div className="pointer-events-none relative h-full w-[320px]" />
        </SwiperSlide>
      )}

      {gallery &&
        gallery.map(
          ({ node }, index, nodes) =>
            node.mediaType === 'image' && (
              <SwiperSlide key={node.id}>
                <div
                  className={cn(
                    'pointer-events-auto relative h-full bg-white',
                    {
                      'after:absolute after:top-0 after:left-full after:block after:h-full after:w-2 after:bg-white':
                        index < nodes.length - 1,
                    }
                  )}
                >
                  <Link
                    href={routeGenerator.property({
                      slug,
                      category: getMainPropertyCategory(property) || '',
                    })}
                    aria-label={property.title}
                  >
                    <Media
                      media={{
                        type: 'image',
                        image: { node },
                      }}
                      imageProps={{
                        wpSize: '1536x1536',
                        width: node.mediaDetails.width,
                        height: node.mediaDetails.height,
                        loading: 'lazy',
                        className:
                          'h-[320px] max-lg:w-auto lg:h-full lg:max-h-[calc(100svh-160px)] max-w-[calc(100svw-16px)] object-cover',
                      }}
                    />
                  </Link>
                </div>
              </SwiperSlide>
            )
        )}

      {isDesktop && (
        <SwiperSlide>
          <div className="pointer-events-none relative h-full w-[320px]" />
        </SwiperSlide>
      )}

      <button
        type="button"
        className="swiper-button--prev text-brand-primary pointer-events-auto absolute top-1/2 left-4 z-20 flex -translate-y-1/2 cursor-pointer items-center justify-center bg-white/40 p-2 backdrop-blur-sm transition-all lg:left-10 [&.swiper-button-disabled]:pointer-events-none [&.swiper-button-disabled]:scale-0 [&.swiper-button-disabled]:opacity-0"
        aria-label="Image précédente"
      >
        <TriangleLeft className="h-6 w-6" />
      </button>

      <button
        type="button"
        className="swiper-button--next text-brand-primary pointer-events-auto absolute top-1/2 right-4 z-20 flex -translate-y-1/2 cursor-pointer items-center justify-center bg-white/40 p-2 backdrop-blur-sm transition-all lg:right-10 [&.swiper-button-disabled]:pointer-events-none [&.swiper-button-disabled]:scale-0 [&.swiper-button-disabled]:opacity-0"
        aria-label="Image suivante"
      >
        <TriangleRight className="h-6 w-6" />
      </button>
    </Swiper>
  );
};

export default PropertyTeaseSwiper;
